import { falArrowDownWideShort } from 'shared/@generated/icons/falArrowDownWideShort';
import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import { useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, Breed } from 'shared/@generated/models';
import { BaseModalRef } from 'shared/components/base-modal';
import { BreedPickerPanel } from 'shared/components/breed-picker-panel';
import { DropdownSelect } from 'shared/components/form/dropdown-select';
import { TextInput } from 'shared/components/form/text-input';

import { Text } from 'shared/components/text';
import {
  ActiveFilterItem,
  ActiveFilterList,
  FilterButton,
  FilterButtonRow,
  FilterModal,
  FilterModalFooter,
  FilterOverviewRow,
  SelectFilterPanel,
} from 'shared/components/web-filter-controls';
import { useBreeds } from 'shared/state/breeds';
import { filterBreederBreedOptions, useBreedersFilterStore } from 'shared/state/breeders-filter';

type FilterPanel = 'overview' | 'breeds' | 'animal' | 'query';

type BreederFilterValues = {
  breeds: (number | string)[];
  animal: Animal | null;
  query: string;
};

export type ControlledBreedersWebFilterControlsProps = BreederFilterValues & {
  totalBreeders: number;
  showAnimal?: boolean;
  showQuery?: boolean;
  multipleBreeds?: boolean;
  allowBreedQuery?: boolean;
  floating?: boolean;
  lockBreed?: boolean;
  lockAnimal?: boolean;
  onBreedsChange: (breeds: (number | string)[]) => void;
  onAnimalChange: (animal: Animal | null) => void;
  onQueryChange: (query: string) => void;
  onClear: () => void;
};

export function BreedersWebFilterControls({
  totalBreeders,
  animal,
}: {
  totalBreeders: number;
  animal?: Animal;
}) {
  const filter = useBreedersFilterStore((state) => state.filter);
  const setFilter = useBreedersFilterStore((state) => state.setFilter);
  const reset = useBreedersFilterStore((state) => state.reset);

  return (
    <ControlledBreedersWebFilterControls
      breeds={filter.breeds}
      animal={animal ?? null}
      query={''}
      totalBreeders={totalBreeders}
      multipleBreeds={true}
      allowBreedQuery={false}
      onBreedsChange={(breeds) => setFilter({ breeds })}
      onAnimalChange={() => undefined}
      onQueryChange={() => undefined}
      onClear={reset}
    />
  );
}

export function ControlledBreedersWebFilterControls({
  breeds: selectedBreedValues,
  animal,
  query,
  totalBreeders,
  showAnimal = false,
  showQuery = false,
  multipleBreeds = true,
  allowBreedQuery = true,
  floating = false,
  lockBreed = false,
  lockAnimal = false,
  onBreedsChange,
  onAnimalChange,
  onQueryChange,
  onClear,
}: ControlledBreedersWebFilterControlsProps) {
  const { t } = useTranslation(['components', 'common']);
  const modalRef = useRef<BaseModalRef>(null);
  const [panel, setPanel] = useState<FilterPanel>('overview');
  const [breedQuery, setBreedQuery] = useState('');
  const { breeds, loading } = useBreeds();

  const availableBreeds = useMemo(
    () => filterBreederBreedOptions(breeds, animal, breedQuery),
    [animal, breedQuery, breeds],
  );

  const selectedBreeds = useMemo(
    () =>
      selectedBreedValues.map((value) => ({
        value,
        label:
          typeof value === 'number'
            ? (breeds.find((breed) => breed.id === value)?.name ??
              t('breeders_filter_form.selected_breed'))
            : value,
      })),
    [breeds, selectedBreedValues, t],
  );

  const animalOptions = [
    { label: t('breeders_filter_form.all_animals'), value: null },
    ...Object.values(Animal).map((value) => ({
      label: t(`common:enums.animal.${value}`),
      value,
    })),
  ];
  const selectedAnimalLabel =
    animalOptions.find((option) => option.value === animal)?.label ?? animalOptions[0]!.label;
  const selectedQueryLabel = query.trim() || t('breeders_filter_form.all_breeders');
  const activeCategoryCount =
    (!lockBreed && selectedBreedValues.length > 0 ? 1 : 0) +
    (showAnimal && !lockAnimal && animal ? 1 : 0) +
    (showQuery && query.trim() ? 1 : 0);

  function openPanel(nextPanel: FilterPanel) {
    setBreedQuery('');
    setPanel(nextPanel);
    modalRef.current?.open();
  }

  function toggleBreed(breed: Breed) {
    if (lockBreed) {
      return;
    }

    if (!multipleBreeds) {
      onBreedsChange([breed.id]);
      return;
    }

    onBreedsChange(
      selectedBreedValues.includes(breed.id)
        ? selectedBreedValues.filter((value) => value !== breed.id)
        : [...selectedBreedValues, breed.id],
    );
  }

  function addBreedQuery(value: string) {
    if (!selectedBreedValues.includes(value)) {
      onBreedsChange([...selectedBreedValues, value]);
    }
  }

  function removeBreed(value: string | number) {
    if (lockBreed) {
      return;
    }

    onBreedsChange(selectedBreedValues.filter((breed) => breed !== value));
  }

  const activeFilters: ActiveFilterItem[] = [
    ...(!lockBreed
      ? selectedBreeds.map((breed) => ({
          key: `breed-${String(breed.value)}`,
          label: breed.label,
          onRemove: () => removeBreed(breed.value),
        }))
      : []),
    ...(showAnimal && !lockAnimal && animal
      ? [
          {
            key: 'animal',
            label: selectedAnimalLabel,
            onRemove: () => onAnimalChange(null),
          },
        ]
      : []),
    ...(showQuery && query.trim()
      ? [
          {
            key: 'query',
            label: query.trim(),
            onRemove: () => onQueryChange(''),
          },
        ]
      : []),
  ];

  const modalTitle =
    panel === 'overview'
      ? t('breeders_filter_form.all_filters')
      : {
          breeds: t('breeders_filter_form.breed'),
          animal: t('breeders_filter_form.animal'),
          query: t('breeders_filter_form.kennel_name'),
        }[panel];

  const filterModal = (
    <FilterModal
      ref={modalRef}
      title={modalTitle}
      overview={panel === 'overview'}
      backLabel={t('breeders_filter_form.all_filters')}
      onBack={() => setPanel('overview')}
      contentTestID={'breeders-filter-modal'}
      footer={({ close }) => (
        <FilterModalFooter
          clearLabel={t('common:clear')}
          applyLabel={t('breeders_filter_form.view_breeders')}
          resultCount={totalBreeders}
          clearDisabled={activeCategoryCount === 0}
          clearTestID={'breeders-filter-clear'}
          applyTestID={'breeders-filter-apply'}
          onClear={onClear}
          onApply={close}
        />
      )}
    >
      <View className={'min-h-0 flex-1'} testID={'breeders-filter-form'}>
        {panel !== 'overview' && (
          <Text className={'sr-only'}>{t('breeders_filter_form.filter_breeders')}</Text>
        )}

        {panel === 'overview' ? (
          <ScrollView contentContainerClassName={'p-3 md:px-5 md:py-4'}>
            {!lockBreed && (
              <FilterOverviewRow
                label={t('breeders_filter_form.breed')}
                value={
                  selectedBreeds.length > 0
                    ? selectedBreeds.map((breed) => breed.label).join(', ')
                    : t('breeders_filter_form.all_breeds')
                }
                active={selectedBreeds.length > 0}
                testID={'breed-input-trigger'}
                onPress={() => setPanel('breeds')}
              />
            )}

            {showAnimal && !lockAnimal && (
              <FilterOverviewRow
                label={t('breeders_filter_form.animal')}
                value={selectedAnimalLabel}
                active={Boolean(animal)}
                onPress={() => setPanel('animal')}
              />
            )}

            {showQuery && (
              <FilterOverviewRow
                label={t('breeders_filter_form.kennel_name')}
                value={selectedQueryLabel}
                active={Boolean(query.trim())}
                onPress={() => setPanel('query')}
              />
            )}
          </ScrollView>
        ) : (
          <View className={'min-h-0 flex-1'}>
            {panel === 'breeds' && (
              <BreedPickerPanel
                query={breedQuery}
                setQuery={setBreedQuery}
                breeds={availableBreeds}
                allBreeds={breeds}
                loading={loading}
                selected={selectedBreedValues}
                multiple={multipleBreeds}
                allowQuery={allowBreedQuery}
                onToggle={toggleBreed}
                onRemove={removeBreed}
                onSelectQuery={addBreedQuery}
                optionTestID={(breed) => `breed-input-option-${breed.slug}`}
              />
            )}

            {panel === 'animal' && (
              <SelectFilterPanel options={animalOptions} value={animal} onChange={onAnimalChange} />
            )}

            {panel === 'query' && (
              <View className={'px-3 py-5 md:px-5'}>
                <TextInput
                  dense={true}
                  autoFocus={true}
                  placeholder={t('breeders_filter_form.search_by_name')}
                  value={query}
                  onChangeText={onQueryChange}
                />
              </View>
            )}
          </View>
        )}
      </View>
    </FilterModal>
  );

  if (floating) {
    return (
      <>
        <FilterButton
          label={t('breeders_filter_form.all_filters')}
          icon={falSlidersSimple}
          count={activeCategoryCount}
          active={activeCategoryCount > 0}
          testID={'breeders-filter-trigger'}
          onPress={() => openPanel('overview')}
        />
        {filterModal}
      </>
    );
  }

  return (
    <View className={'mt-3 md:mt-4'}>
      <FilterButtonRow>
        <FilterButton
          label={t('breeders_filter_form.all_filters')}
          icon={falSlidersSimple}
          count={activeCategoryCount}
          active={activeCategoryCount > 0}
          testID={'breeders-filter-trigger'}
          onPress={() => openPanel('overview')}
        />
        {!lockBreed && (
          <FilterButton
            label={t('breeders_filter_form.breed')}
            count={selectedBreeds.length}
            active={selectedBreeds.length > 0}
            onPress={() => openPanel('breeds')}
          />
        )}
      </FilterButtonRow>

      <ActiveFilterList items={activeFilters} clearLabel={t('common:clear')} onClear={onClear} />

      <View className={'mt-3 flex-row items-center pb-2 md:py-2'}>
        <Text
          className={'text-brand-brown-dark min-w-0 flex-1 text-sm font-semibold md:text-base'}
          accessibilityLiveRegion={'polite'}
          numberOfLines={1}
        >
          {t('breeders_filter_form.matches_found', { count: totalBreeders })}
        </Text>

        <DropdownSelect<string>
          title={t('breeders_filter_form.sort.recommended')}
          options={[
            {
              label: t('breeders_filter_form.sort.recommended'),
              value: null,
            },
          ]}
          value={null}
          dense={true}
          bold={true}
          contentAlign={'end'}
          triggerClassName={'flex-none'}
          triggerIcon={falArrowDownWideShort}
          onChange={() => undefined}
        />
      </View>

      {filterModal}
    </View>
  );
}
